// src/pages/platform/PlatformSettingsPage.tsx
// Platform settings page with tabs: Settings, Branding, Limits & Usage.

import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { usePlatform, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  ErrorAlert,
} from '@react/components';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SettingsTab = 'settings' | 'branding' | 'limits';

interface SettingsForm {
  allow_self_registration: boolean;
  require_email_verification: boolean;
  allow_tenant_creation: boolean;
  allow_product_registration: boolean;
  require_2fa: boolean;
  sso_enabled: boolean;
  sso_provider: string;
  allowed_domains: string;
  allowed_ip_ranges: string;
  session_timeout_minutes: number;
  max_failed_login_attempts: number;
  enable_api_access: boolean;
  enable_webhooks: boolean;
  enable_integrations: boolean;
  enable_audit_logging: boolean;
  enable_billing: boolean;
  enable_federation: boolean;
  default_timezone: string;
  default_locale: string;
  data_retention_days: number;
}

interface BrandingForm {
  logo_url: string;
  favicon_url: string;
  primary_color: string;
  secondary_color: string;
  custom_domain: string;
  support_email: string;
  support_url: string;
  terms_url: string;
  privacy_url: string;
}

interface LimitsForm {
  max_tenants: number;
  max_users: number;
  max_products: number;
  max_storage_bytes: number;
  max_api_calls_per_month: number;
}

interface UsageData {
  current_tenants: number;
  current_users: number;
  current_products: number;
  current_storage_bytes: number;
  current_api_calls_this_month: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS: { id: SettingsTab; label: string; key: string }[] = [
  { id: 'settings', label: 'Settings', key: 'platform.settingsTab' },
  { id: 'branding', label: 'Branding', key: 'platform.brandingTab' },
  { id: 'limits', label: 'Limits & Usage', key: 'platform.limitsTab' },
];

const DEFAULT_SETTINGS: SettingsForm = {
  allow_self_registration: false,
  require_email_verification: true,
  allow_tenant_creation: false,
  allow_product_registration: false,
  require_2fa: false,
  sso_enabled: false,
  sso_provider: '',
  allowed_domains: '',
  allowed_ip_ranges: '',
  session_timeout_minutes: 60,
  max_failed_login_attempts: 5,
  enable_api_access: false,
  enable_webhooks: false,
  enable_integrations: false,
  enable_audit_logging: false,
  enable_billing: false,
  enable_federation: false,
  default_timezone: 'UTC',
  default_locale: 'en',
  data_retention_days: 365,
};

const DEFAULT_BRANDING: BrandingForm = {
  logo_url: '',
  favicon_url: '',
  primary_color: '#3B82F6',
  secondary_color: '#6B7280',
  custom_domain: '',
  support_email: '',
  support_url: '',
  terms_url: '',
  privacy_url: '',
};

const DEFAULT_LIMITS: LimitsForm = {
  max_tenants: 0,
  max_users: 0,
  max_products: 0,
  max_storage_bytes: 0,
  max_api_calls_per_month: 0,
};

const DEFAULT_USAGE: UsageData = {
  current_tenants: 0,
  current_users: 0,
  current_products: 0,
  current_storage_bytes: 0,
  current_api_calls_this_month: 0,
};

// ---------------------------------------------------------------------------
// Toggle Switch
// ---------------------------------------------------------------------------

function ToggleSwitch({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex items-center justify-between py-3">
      <div>
        <span className="text-sm font-medium text-foreground">{label}</span>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-primary' : 'bg-muted'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const PlatformSettingsPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = usePlatform();
  const { t } = useI18n();

  const [activeTab, setActiveTab] = useState<SettingsTab>('settings');

  // Form state per tab
  const [settingsForm, setSettingsForm] = useState<SettingsForm>(DEFAULT_SETTINGS);
  const [brandingForm, setBrandingForm] = useState<BrandingForm>(DEFAULT_BRANDING);
  const [limitsForm, setLimitsForm] = useState<LimitsForm>(DEFAULT_LIMITS);
  const [usage, setUsage] = useState<UsageData>(DEFAULT_USAGE);

  // Transfer ownership
  const [transferOwnerProfileId, setTransferOwnerProfileId] = useState('');
  const [transferConfirm, setTransferConfirm] = useState(false);

  // Feedback
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (id) {
      store.GetPlatform(id);
      store.GetPlatformSettings(id);
      store.GetPlatformBranding(id);
      store.GetPlatformLimits(id);
      store.GetPlatformUsage(id);
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);

  // Populate settings form
  useEffect(() => {
    const item = store.currentItem as any;
    if (!item) return;

    // Settings
    const s = item.settings ?? {};
    setSettingsForm({
      allow_self_registration: s.allow_self_registration ?? false,
      require_email_verification: s.require_email_verification ?? true,
      allow_tenant_creation: s.allow_tenant_creation ?? false,
      allow_product_registration: s.allow_product_registration ?? false,
      require_2fa: s.require_2fa ?? false,
      sso_enabled: s.sso_enabled ?? false,
      sso_provider: s.sso_provider ?? '',
      allowed_domains: Array.isArray(s.allowed_domains) ? s.allowed_domains.join(', ') : (s.allowed_domains ?? ''),
      allowed_ip_ranges: Array.isArray(s.allowed_ip_ranges) ? s.allowed_ip_ranges.join(', ') : (s.allowed_ip_ranges ?? ''),
      session_timeout_minutes: s.session_timeout_minutes ?? 60,
      max_failed_login_attempts: s.max_failed_login_attempts ?? 5,
      enable_api_access: s.enable_api_access ?? false,
      enable_webhooks: s.enable_webhooks ?? false,
      enable_integrations: s.enable_integrations ?? false,
      enable_audit_logging: s.enable_audit_logging ?? false,
      enable_billing: s.enable_billing ?? false,
      enable_federation: s.enable_federation ?? false,
      default_timezone: s.default_timezone ?? 'UTC',
      default_locale: s.default_locale ?? 'en',
      data_retention_days: s.data_retention_days ?? 365,
    });

    // Branding
    const b = item.branding ?? {};
    setBrandingForm({
      logo_url: b.logo_url ?? '',
      favicon_url: b.favicon_url ?? '',
      primary_color: b.primary_color ?? '#3B82F6',
      secondary_color: b.secondary_color ?? '#6B7280',
      custom_domain: b.custom_domain ?? '',
      support_email: b.support_email ?? '',
      support_url: b.support_url ?? '',
      terms_url: b.terms_url ?? '',
      privacy_url: b.privacy_url ?? '',
    });

    // Limits
    const l = item.limits ?? {};
    setLimitsForm({
      max_tenants: l.max_tenants ?? 0,
      max_users: l.max_users ?? 0,
      max_products: l.max_products ?? 0,
      max_storage_bytes: l.max_storage_bytes ?? 0,
      max_api_calls_per_month: l.max_api_calls_per_month ?? 0,
    });

    // Usage (read-only)
    setUsage({
      current_tenants: l.current_tenants ?? 0,
      current_users: l.current_users ?? 0,
      current_products: l.current_products ?? 0,
      current_storage_bytes: l.current_storage_bytes ?? 0,
      current_api_calls_this_month: l.current_api_calls_this_month ?? 0,
    });
  }, [store.currentItem]);

  const platformName = (store.currentItem as any)?.name || t('platform.title');

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const clearFeedback = () => {
    setSaveSuccess(null);
    setSaveError(null);
  };

  const handleSettingsChange = <K extends keyof SettingsForm>(key: K, value: SettingsForm[K]) => {
    setSettingsForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleBrandingChange = <K extends keyof BrandingForm>(key: K, value: BrandingForm[K]) => {
    setBrandingForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleLimitsChange = <K extends keyof LimitsForm>(key: K, value: LimitsForm[K]) => {
    setLimitsForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    if (!id) return;
    clearFeedback();
    setSubmitting(true);
    try {
      const payload = {
        ...settingsForm,
        allowed_domains: settingsForm.allowed_domains
          .split(',')
          .map((d) => d.trim())
          .filter(Boolean),
        allowed_ip_ranges: settingsForm.allowed_ip_ranges
          .split(',')
          .map((r) => r.trim())
          .filter(Boolean),
      };
      await store.UpdatePlatformSettings(id, { settings: payload });
      setSaveSuccess(t('platform.settingsSaved'));
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err) {
      setSaveError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveBranding = async () => {
    if (!id) return;
    clearFeedback();
    setSubmitting(true);
    try {
      await store.UpdatePlatformBranding(id, { branding: brandingForm });
      setSaveSuccess(t('platform.brandingSaved'));
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err) {
      setSaveError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveLimits = async () => {
    if (!id) return;
    clearFeedback();
    setSubmitting(true);
    try {
      await store.UpdatePlatformLimits(id, { limits: limitsForm as any });
      setSaveSuccess(t('platform.limitsSaved'));
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err) {
      setSaveError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTransferOwnership = async () => {
    if (!id || !transferOwnerProfileId.trim()) return;
    if (!transferConfirm) {
      setTransferConfirm(true);
      return;
    }
    clearFeedback();
    setSubmitting(true);
    try {
      await store.TransferPlatformOwnership(id, {
        new_owner: { id: transferOwnerProfileId.trim() } as any,
      });
      setSaveSuccess(t('platform.ownershipTransferred'));
      setTransferOwnerProfileId('');
      setTransferConfirm(false);
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err) {
      setSaveError(err);
      setTransferConfirm(false);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  if (store.isLoading && !store.currentItem) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Usage helpers
  // ---------------------------------------------------------------------------

  function usagePercentage(current: number, max: number): number {
    if (max === 0) return 0; // unlimited
    return Math.min(100, Math.round((current / max) * 100));
  }

  function usageLabel(current: number, max: number, isBytes?: boolean): string {
    const fmt = isBytes ? formatBytes : formatNumber;
    if (max === 0) return `${fmt(current)} / Unlimited`;
    return `${fmt(current)} / ${fmt(max)}`;
  }

  // ---------------------------------------------------------------------------
  // Tab: Settings
  // ---------------------------------------------------------------------------

  const renderSettingsTab = () => (
    <div className="space-y-6">
      {/* Registration & Access */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.registrationAccess')}</CardTitle>
          <CardDescription>Control how users and tenants can register and access the platform.</CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <ToggleSwitch
            checked={settingsForm.allow_self_registration}
            onChange={(v) => handleSettingsChange('allow_self_registration', v)}
            label="Allow Self Registration"
            description="Users can create accounts without an invitation."
          />
          <ToggleSwitch
            checked={settingsForm.require_email_verification}
            onChange={(v) => handleSettingsChange('require_email_verification', v)}
            label="Require Email Verification"
            description="New users must verify their email before accessing the platform."
          />
          <ToggleSwitch
            checked={settingsForm.allow_tenant_creation}
            onChange={(v) => handleSettingsChange('allow_tenant_creation', v)}
            label="Allow Tenant Creation"
            description="Users can create new tenants (organizations) on this platform."
          />
          <ToggleSwitch
            checked={settingsForm.allow_product_registration}
            onChange={(v) => handleSettingsChange('allow_product_registration', v)}
            label="Allow Product Registration"
            description="Third-party products can be registered on this platform."
          />
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.security')}</CardTitle>
          <CardDescription>Authentication and access security settings.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="divide-y divide-border">
            <ToggleSwitch
              checked={settingsForm.require_2fa}
              onChange={(v) => handleSettingsChange('require_2fa', v)}
              label="Require Two-Factor Authentication"
              description="All users must enable 2FA to access the platform."
            />
            <ToggleSwitch
              checked={settingsForm.sso_enabled}
              onChange={(v) => handleSettingsChange('sso_enabled', v)}
              label="Enable SSO"
              description="Allow single sign-on via external identity provider."
            />
          </div>
          {settingsForm.sso_enabled && (
            <Input
              label="SSO Provider"
              type="text"
              value={settingsForm.sso_provider}
              onChange={(e) => handleSettingsChange('sso_provider', e.target.value)}
              placeholder="e.g. okta, azure-ad, google"
            />
          )}
          <Input
            label="Allowed Domains"
            type="text"
            value={settingsForm.allowed_domains}
            onChange={(e) => handleSettingsChange('allowed_domains', e.target.value)}
            placeholder="example.com, company.io"
          />
          <p className="text-xs text-muted-foreground -mt-2">
            Comma-separated list of email domains allowed to register. Leave empty for no restriction.
          </p>
          <Input
            label="Allowed IP Ranges"
            type="text"
            value={settingsForm.allowed_ip_ranges}
            onChange={(e) => handleSettingsChange('allowed_ip_ranges', e.target.value)}
            placeholder="10.0.0.0/8, 192.168.1.0/24"
          />
          <p className="text-xs text-muted-foreground -mt-2">
            Comma-separated CIDR ranges. Leave empty for no IP restriction.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Session Timeout (minutes)"
              type="number"
              value={String(settingsForm.session_timeout_minutes)}
              onChange={(e) =>
                handleSettingsChange('session_timeout_minutes', parseInt(e.target.value, 10) || 0)
              }
              placeholder="60"
            />
            <Input
              label="Max Failed Login Attempts"
              type="number"
              value={String(settingsForm.max_failed_login_attempts)}
              onChange={(e) =>
                handleSettingsChange('max_failed_login_attempts', parseInt(e.target.value, 10) || 0)
              }
              placeholder="5"
            />
          </div>
        </CardContent>
      </Card>

      {/* Features */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.features')}</CardTitle>
          <CardDescription>Enable or disable platform-level features.</CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <ToggleSwitch
            checked={settingsForm.enable_api_access}
            onChange={(v) => handleSettingsChange('enable_api_access', v)}
            label="API Access"
            description="Allow programmatic access via REST/gRPC APIs."
          />
          <ToggleSwitch
            checked={settingsForm.enable_webhooks}
            onChange={(v) => handleSettingsChange('enable_webhooks', v)}
            label="Webhooks"
            description="Enable outbound webhook notifications for events."
          />
          <ToggleSwitch
            checked={settingsForm.enable_integrations}
            onChange={(v) => handleSettingsChange('enable_integrations', v)}
            label="Integrations"
            description="Allow third-party integrations and connectors."
          />
          <ToggleSwitch
            checked={settingsForm.enable_audit_logging}
            onChange={(v) => handleSettingsChange('enable_audit_logging', v)}
            label="Audit Logging"
            description="Record all administrative and security-related actions."
          />
          <ToggleSwitch
            checked={settingsForm.enable_billing}
            onChange={(v) => handleSettingsChange('enable_billing', v)}
            label="Billing"
            description="Enable billing, invoicing, and subscription management."
          />
          <ToggleSwitch
            checked={settingsForm.enable_federation}
            onChange={(v) => handleSettingsChange('enable_federation', v)}
            label="Federation"
            description="Allow cross-platform data federation and resource sharing."
          />
        </CardContent>
      </Card>

      {/* Data */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.dataSettings')}</CardTitle>
          <CardDescription>Localization and data retention defaults.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="Default Timezone"
            type="text"
            value={settingsForm.default_timezone}
            onChange={(e) => handleSettingsChange('default_timezone', e.target.value)}
            placeholder="UTC"
          />
          <Input
            label="Default Locale"
            type="text"
            value={settingsForm.default_locale}
            onChange={(e) => handleSettingsChange('default_locale', e.target.value)}
            placeholder="en"
          />
          <div>
            <Input
              label="Data Retention (days)"
              type="number"
              value={String(settingsForm.data_retention_days)}
              onChange={(e) =>
                handleSettingsChange('data_retention_days', parseInt(e.target.value, 10) || 0)
              }
              placeholder="365"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Number of days to retain data before automatic cleanup. Set to 0 for indefinite retention.
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleSaveSettings} disabled={submitting}>
            {submitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Tab: Branding
  // ---------------------------------------------------------------------------

  const renderBrandingTab = () => (
    <div className="space-y-6">
      {/* Logo & Icons */}
      <Card>
        <CardHeader>
          <CardTitle>Logo & Icons</CardTitle>
          <CardDescription>Brand imagery for the platform.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="Logo URL"
            type="text"
            value={brandingForm.logo_url}
            onChange={(e) => handleBrandingChange('logo_url', e.target.value)}
            placeholder="https://example.com/logo.svg"
          />
          <Input
            label="Favicon URL"
            type="text"
            value={brandingForm.favicon_url}
            onChange={(e) => handleBrandingChange('favicon_url', e.target.value)}
            placeholder="https://example.com/favicon.ico"
          />
        </CardContent>
      </Card>

      {/* Colors */}
      <Card>
        <CardHeader>
          <CardTitle>Colors</CardTitle>
          <CardDescription>Primary and secondary brand colors.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Primary Color</label>
            <div className="flex items-center gap-3">
              <Input
                type="text"
                value={brandingForm.primary_color}
                onChange={(e) => handleBrandingChange('primary_color', e.target.value)}
                placeholder="#3B82F6"
              />
              <div
                className="h-10 w-10 rounded-md border border-border flex-shrink-0"
                style={{ backgroundColor: brandingForm.primary_color }}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Secondary Color</label>
            <div className="flex items-center gap-3">
              <Input
                type="text"
                value={brandingForm.secondary_color}
                onChange={(e) => handleBrandingChange('secondary_color', e.target.value)}
                placeholder="#6B7280"
              />
              <div
                className="h-10 w-10 rounded-md border border-border flex-shrink-0"
                style={{ backgroundColor: brandingForm.secondary_color }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Domain */}
      <Card>
        <CardHeader>
          <CardTitle>Custom Domain</CardTitle>
          <CardDescription>Configure a custom domain for your platform.</CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            label="Custom Domain"
            type="text"
            value={brandingForm.custom_domain}
            onChange={(e) => handleBrandingChange('custom_domain', e.target.value)}
            placeholder="app.yourcompany.com"
          />
        </CardContent>
      </Card>

      {/* Support */}
      <Card>
        <CardHeader>
          <CardTitle>Support</CardTitle>
          <CardDescription>Contact and support information displayed to users.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="Support Email"
            type="email"
            value={brandingForm.support_email}
            onChange={(e) => handleBrandingChange('support_email', e.target.value)}
            placeholder="support@yourcompany.com"
          />
          <Input
            label="Support URL"
            type="text"
            value={brandingForm.support_url}
            onChange={(e) => handleBrandingChange('support_url', e.target.value)}
            placeholder="https://support.yourcompany.com"
          />
        </CardContent>
      </Card>

      {/* Legal */}
      <Card>
        <CardHeader>
          <CardTitle>Legal</CardTitle>
          <CardDescription>Links to legal documents.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="Terms of Service URL"
            type="text"
            value={brandingForm.terms_url}
            onChange={(e) => handleBrandingChange('terms_url', e.target.value)}
            placeholder="https://yourcompany.com/terms"
          />
          <Input
            label="Privacy Policy URL"
            type="text"
            value={brandingForm.privacy_url}
            onChange={(e) => handleBrandingChange('privacy_url', e.target.value)}
            placeholder="https://yourcompany.com/privacy"
          />
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleSaveBranding} disabled={submitting}>
            {submitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Tab: Limits & Usage
  // ---------------------------------------------------------------------------

  const limitItems: {
    key: keyof LimitsForm;
    usageKey: keyof UsageData;
    label: string;
    isBytes?: boolean;
  }[] = [
    { key: 'max_tenants', usageKey: 'current_tenants', label: 'Tenants' },
    { key: 'max_users', usageKey: 'current_users', label: 'Users' },
    { key: 'max_products', usageKey: 'current_products', label: 'Products' },
    { key: 'max_storage_bytes', usageKey: 'current_storage_bytes', label: 'Storage', isBytes: true },
    { key: 'max_api_calls_per_month', usageKey: 'current_api_calls_this_month', label: 'API Calls / Month' },
  ];

  const renderLimitsTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Limits & Usage</CardTitle>
          <CardDescription>
            Set resource limits and monitor current usage. A limit of 0 means unlimited.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {limitItems.map((item) => {
            const current = usage[item.usageKey];
            const max = limitsForm[item.key];
            const pct = usagePercentage(current, max);
            const label = usageLabel(current, max, item.isBytes);

            return (
              <div key={item.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">{item.label}</label>
                  <span className="text-xs text-muted-foreground">{label}</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="h-2 rounded-full bg-muted">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          pct >= 90 ? 'bg-destructive' : pct >= 75 ? 'bg-warning' : 'bg-primary'
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <Input
                    type="number"
                    value={String(max)}
                    onChange={(e) =>
                      handleLimitsChange(item.key, parseInt(e.target.value, 10) || 0)
                    }
                    placeholder="0"
                    className="w-32"
                  />
                </div>
                {pct >= 90 && max > 0 && (
                  <p className="text-xs text-destructive">
                    Usage is at {pct}% of the limit.
                  </p>
                )}
              </div>
            );
          })}
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleSaveLimits} disabled={submitting}>
            {submitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>

      {/* Danger Zone: Transfer Ownership */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription>
            Transfer platform ownership to another user. This action cannot be undone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="New Owner Profile ID"
            type="text"
            value={transferOwnerProfileId}
            onChange={(e) => {
              setTransferOwnerProfileId(e.target.value);
              setTransferConfirm(false);
            }}
            placeholder="Enter profile ID of the new owner"
          />
          {transferConfirm && (
            <Alert variant="warning">
              Are you sure you want to transfer ownership? Click the button again to confirm.
            </Alert>
          )}
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button
            variant="danger"
            onClick={handleTransferOwnership}
            disabled={submitting || !transferOwnerProfileId.trim()}
          >
            {transferConfirm ? 'Confirm Transfer' : 'Transfer Ownership'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Breadcrumb & Header */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/platforms" className="text-muted-foreground hover:text-foreground">
            {t('platform.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/platforms/${id}`} className="text-muted-foreground hover:text-foreground">
            {platformName}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('platform.settings')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{platformName} — {t('platform.settings')}</h1>
      </div>

      {/* Alerts */}
      {saveSuccess && (
        <Alert variant="success" dismissible onDismiss={() => setSaveSuccess(null)}>
          {saveSuccess}
        </Alert>
      )}
      {saveError ? (
        <ErrorAlert error={saveError} dismissible onDismiss={() => setSaveError(null)} />
      ) : null}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Tab Navigation */}
      <div className="border-b border-border mb-6">
        <div className="flex gap-0 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {t(tab.key) !== tab.key ? t(tab.key) : tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'settings' && renderSettingsTab()}
      {activeTab === 'branding' && renderBrandingTab()}
      {activeTab === 'limits' && renderLimitsTab()}
    </div>
  );
});

export default PlatformSettingsPage;
